import { find, flatten, get, map, toNumber, values } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Center, Column } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { Title } from "@/shared/ui/Title";
import { WisnioProfileHighlightsSection } from "@/features/profile/components/WisnioProfileHighlightsSection";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { PersonalityMapSection } from "@/shared/widgets/PersonalityMapSection";
import { ProfileValuesSection } from "@/features/profile/components/ProfileValuesSection";
import { useDimensionsWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { ProfileSummarySection } from "@/features/profile/components/ProfileSummarySection";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { MembersRow } from "@/shared/widgets/MembersRow";
import { Person } from "@/features/people/graphql/generated";
import ProfileLayout from "@/features/profile/components/ProfileLayout";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { PDFExportTrigger } from "@/shared/pdf";
import ProfileSectionWrapper from "@/features/profile/components/ProfileLayout/ProfileSectionWrapper";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import { useTeamNotes } from "@/features/teams/hooks/useTeamNotes";
import {
  useProfileCompletion,
  useProfileLayoutProps,
} from "@/features/profile/hooks";
import { MemberInTeamProfilePdf } from "@/shared/pdf/Templates/Profiles";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel } from "@/shared/constants";
import usePerson from "@/features/people/hooks/usePerson";
import { ResourceEnum } from "@/shared/constants/resources";
import { useTeamProfileData } from "@/features/teams/hooks/useTeamProfileData";
import { CompetencyInsights } from "@/shared/widgets/InsightsSection/CompetencyInsights";

function MemberInTeamProfileScreen() {
  const { teamId: _teamId, memberId: _memberId } = useParams();

  const teamId = toNumber(_teamId);
  const memberId = toNumber(_memberId);
  const navigate = useOrgNavigate();

  const {
    team,
    teamMembers,
    loading: loadingTeamData,
  } = useTeamProfileData(teamId);

  const { validateAccessToResource } = useViewerPermissions();

  const hasPermissionsToEdit = validateAccessToResource({
    resourceType: ResourceEnum.team,
    accessLevel: AccessLevel.full,
    resourceId: teamId,
  });

  const surveyConfig = get(team, "config.dimensions", []);

  const {
    person: member,
    loading: loadingPerson,
    handleUpsertPerson,
  } = usePerson(memberId);

  const {
    notes,
    loading: loadingNotes,
    handleUpsertTeamNote,
    handleDeleteTeamNote,
  } = useTeamNotes(teamId, memberId);

  const memberSummary = find(
    notes,
    (n: any) => n.type === "summary" && n.person_id === memberId
  );

  const { dimensionsData, loading: loadingDimensions } =
    useDimensionsWithScores(
      {
        team_id: teamId,
        people: [memberId],
        type: surveyConfig,
        isLoading: !memberId || loadingPerson || !team?.id || loadingTeamData,
      },
      teamMembers
    );

  const { personalityComplete, competenciesComplete, valuesComplete } =
    useProfileCompletion(dimensionsData);

  const { t } = useTranslation();

  const { layoutProps, tagsData, scope, loadingMessage } =
    useProfileLayoutProps({
      contextType: "team_member_profile",
      entity: member,
      dimensionsData,
      surveyConfig,
      people: [member],
      loadingStates: [loadingTeamData, loadingDimensions, loadingPerson],
      additionalScope: { team_id: teamId },
    });

  return (
    <>
      <HeaderContent>
        <IconButton
          variant="outline"
          icon="Chevron"
          color="black"
          label={t("team.back_to_team")}
          onClick={() => navigate(`teams/${teamId}`)}
          iconProps={{ className: "rotate-180" }}
        />
        <PDFExportTrigger
          documentTitle={`${team.name} - ${member.name}`}
          TriggerComponent={(props) => (
            <IconButton
              color="black"
              icon="Print"
              label="Print member profile"
              variant="outline"
              {...props}
            />
          )}
          PDFComponent={
            <MemberInTeamProfilePdf
              summary={memberSummary?.note || ""}
              team={team}
              person={member}
              dimensions={dimensionsData}
            />
          }
        />
      </HeaderContent>
      <Breadcrumbs
        items={[
          {
            label: team.name || "",
            path: `/teams/${team?.id}`,
          },
          {
            label: member.name || "",
            path: `/teams/${team?.id}/member/${member?.id}`,
            options: map(teamMembers, (_person) => ({
              id: `${_person?.id}`,
              label: _person?.name || _person?.email || "",
              path: `/teams/${team?.id}/member/${_person?.id}`,
            })),
          },
        ]}
      />

      <ProfileLayout {...layoutProps} loadingMessage={loadingMessage}>
        <ProfileHeader
          initials={member.initials || ""}
          name={member.name || ""}
          avatarUrl={member.avatar || ""}
          teamName={team?.name || ""}
          onContactChange={handleUpsertPerson}
          canEdit={hasPermissionsToEdit}
          email={member?.email || ""}
          phone={member?.phone || ""}
          linkedin={member?.linkedin || ""}
          isContact={!!member.is_contact}
        />
        <WisnioProfileHighlightsSection dimensions={dimensionsData} />
        <ProfileSectionWrapper label="summary" id="summary">
          <ProfileSummarySection
            onDeleteSummary={() =>
              handleDeleteTeamNote(toNumber(memberSummary?.id), "summary")
            }
            onSaveSummary={(text) =>
              handleUpsertTeamNote({
                note: text,
                type: "summary",
                id: toNumber(memberSummary?.id),
              })
            }
            loading={loadingNotes}
            summaryContent={memberSummary?.note}
            tagsData={tagsData}
            updatedAt={memberSummary?.updated_at}
            flowId={getGptFlowKey("team_member_profile_summary")}
            scope={scope}
          />
        </ProfileSectionWrapper>

        {personalityComplete && (
          <ProfileSectionWrapper label="personality" id="personality">
            <PersonalityMapSection
              dimensions={dimensionsData.personality}
              context="user_in_team"
            />
          </ProfileSectionWrapper>
        )}

        {valuesComplete && (
          <ProfileSectionWrapper label="values" id="values">
            <ProfileValuesSection
              dimensions={dimensionsData.values}
              context="user_in_team"
              defaultPersonId={memberId}
            />
          </ProfileSectionWrapper>
        )}

        {competenciesComplete && (
          <ProfileSectionWrapper label="competencies" id="competencies">
            <CompetencyMapSection
              dimensions={dimensionsData.competencies}
              context="user_in_team"
              defaultPersonId={memberId}
            />
            <CompetencyInsights
              context="user_in_team"
              person={member}
              dimensions={dimensionsData?.competencies}
            />
          </ProfileSectionWrapper>
        )}

        <Column className="gap-8 pb-16">
          <Title
            as="h4"
            description="Learn about other team members to see which values drive their motivation and how their personality contributes to the team's diversity."
          >
            Other members of the team
          </Title>
          <MembersRow
            teamId={teamId}
            exclude={[memberId]}
            members={teamMembers as Person[]}
            dimensionsConfig={surveyConfig}
          />
          <Center>
            <IconButton
              icon="Chevron"
              label={t("team.back_to_team")}
              onClick={() => navigate(`teams/${teamId}`)}
              iconProps={{ className: "rotate-180" }}
            />
          </Center>
        </Column>
      </ProfileLayout>
    </>
  );
}

export default MemberInTeamProfileScreen;
